import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUpIcon, SearchIcon, ChevronRightIcon, ChevronLeftIcon, StarIcon, ArrowUpIcon, ArrowDownIcon, ArrowRightIcon, SparklesIcon, BookOpenIcon, TrophyIcon, FilterIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { usePlatform } from '../context/PlatformContext';
import UnifiedBookCard from '../components/Books/UnifiedBookCard';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
// Extended mock data with more books per genre
const allBestsellers = [{
  id: '1',
  rank: 1,
  title: 'The Silent Echo',
  author: 'Elena Richards',
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.8,
  reviewCount: 1245,
  genres: ['Mystery', 'Thriller'],
  weeksOnList: 4,
  previousRank: 2,
  movement: 'up',
  sales: 5280,
  description: 'A gripping thriller that follows investigative journalist Mira Hayes as she uncovers a conspiracy that could change everything.',
  spiceLevel: 3,
  darknessLevel: 3,
  formats: ['paperback', 'ebook', 'audiobook']
}, {
  id: '2',
  rank: 2,
  title: 'Beyond the Veil',
  author: 'Marcus Chen',
  coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.6,
  reviewCount: 892,
  genres: ['Fantasy', 'Romance'],
  weeksOnList: 6,
  previousRank: 1,
  movement: 'down',
  sales: 4950,
  description: 'Journey through magical worlds and epic love stories in this enchanting fantasy romance.',
  spiceLevel: 4,
  darknessLevel: 2,
  relationshipType: 'Enemies to Lovers',
  formats: ['hardcover', 'ebook', 'audiobook']
}, {
  id: '3',
  rank: 3,
  title: 'Midnight Shadows',
  author: 'Sophia Williams',
  coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.7,
  reviewCount: 756,
  genres: ['Horror', 'Supernatural'],
  weeksOnList: 2,
  previousRank: 5,
  movement: 'up',
  sales: 4320,
  description: 'A terrifying journey into the supernatural that will keep you awake at night.',
  spiceLevel: 2,
  darknessLevel: 5,
  formats: ['paperback', 'ebook']
}, {
  id: '4',
  rank: 4,
  title: 'The Lost City',
  author: 'David Miller',
  coverImage: 'https://images.unsplash.com/photo-1531928351158-2f736078e0a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.5,
  reviewCount: 623,
  genres: ['Adventure', 'Historical Fiction'],
  weeksOnList: 3,
  previousRank: 3,
  movement: 'same',
  sales: 3890,
  description: 'An epic adventure through uncharted territories and ancient civilizations.',
  spiceLevel: 2,
  darknessLevel: 3,
  formats: ['paperback', 'ebook', 'audiobook']
}, {
  id: '5',
  rank: 5,
  title: 'Whispers in the Wind',
  author: 'Emma Johnson',
  coverImage: 'https://images.unsplash.com/photo-1589998059171-988d887df646?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.9,
  reviewCount: 542,
  genres: ['Literary Fiction', 'Drama'],
  weeksOnList: 1,
  previousRank: 0,
  movement: 'new',
  sales: 3650,
  description: 'A moving story about family, loss, and redemption that will touch your heart.',
  spiceLevel: 1,
  darknessLevel: 4,
  formats: ['paperback', 'ebook']
}, {
  id: '6',
  rank: 6,
  title: 'Quantum Dreams',
  author: 'Alex Zhang',
  coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.4,
  reviewCount: 489,
  genres: ['Science Fiction', 'Thriller'],
  weeksOnList: 5,
  previousRank: 4,
  movement: 'down',
  sales: 3210,
  description: 'A mind-bending journey through alternate realities and quantum physics.',
  spiceLevel: 3,
  darknessLevel: 3,
  formats: ['hardcover', 'ebook', 'audiobook']
}, {
  id: '7',
  rank: 7,
  title: 'Hearts Entwined',
  author: 'Sarah Blake',
  coverImage: 'https://images.unsplash.com/photo-1603162525937-92a06da4b8ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.3,
  reviewCount: 412,
  genres: ['Romance', 'Contemporary'],
  weeksOnList: 3,
  previousRank: 8,
  movement: 'up',
  sales: 2980,
  description: 'Two hearts find each other in the most unexpected of places.',
  spiceLevel: 4,
  darknessLevel: 1,
  relationshipType: 'Friends to Lovers',
  formats: ['ebook', 'audiobook']
}, {
  id: '8',
  rank: 8,
  title: 'The Forgotten Kingdom',
  author: 'Michael Torres',
  coverImage: 'https://images.unsplash.com/photo-1614332287897-cdc485fa562d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.6,
  reviewCount: 378,
  genres: ['Fantasy', 'Adventure'],
  weeksOnList: 4,
  previousRank: 6,
  movement: 'down',
  sales: 2760,
  description: 'An ancient kingdom rises from the ashes, and with it, old enemies.',
  spiceLevel: 2,
  darknessLevel: 4,
  formats: ['hardcover', 'paperback', 'ebook']
}, {
  id: '9',
  rank: 9,
  title: 'Dark Waters',
  author: 'James Holloway',
  coverImage: 'https://images.unsplash.com/photo-1629992101753-56d196c8aabb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.5,
  reviewCount: 334,
  genres: ['Thriller', 'Crime'],
  weeksOnList: 2,
  previousRank: 11,
  movement: 'up',
  sales: 2540,
  description: 'A detective races against time to stop a serial killer terrorizing the coast.',
  spiceLevel: 1,
  darknessLevel: 5,
  formats: ['paperback', 'ebook', 'audiobook']
}, {
  id: '10',
  rank: 10,
  title: 'Starfall',
  author: 'Priya Nair',
  coverImage: 'https://images.unsplash.com/photo-1633477189729-9290b3261d0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.7,
  reviewCount: 298,
  genres: ['Science Fiction', 'Romance'],
  weeksOnList: 1,
  previousRank: 0,
  movement: 'new',
  sales: 2310,
  description: 'When two rival starship captains are stranded together, the universe conspires to bring them closer.',
  spiceLevel: 3,
  darknessLevel: 2,
  relationshipType: 'Enemies to Lovers',
  formats: ['ebook', 'audiobook']
}, {
  id: '11',
  rank: 11,
  title: 'The Crimson Rose',
  author: 'Isabelle Fontaine',
  coverImage: 'https://images.unsplash.com/photo-1580934738404-5eb8d4c0389e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.4,
  reviewCount: 267,
  genres: ['Romance', 'Historical Fiction'],
  weeksOnList: 7,
  previousRank: 9,
  movement: 'down',
  sales: 2100,
  description: 'A forbidden love blooms in the court of Versailles.',
  spiceLevel: 3,
  darknessLevel: 2,
  formats: ['paperback', 'ebook']
}, {
  id: '12',
  rank: 12,
  title: 'The Haunting of Ashwood',
  author: 'Clara Voss',
  coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.6,
  reviewCount: 245,
  genres: ['Horror', 'Mystery'],
  weeksOnList: 3,
  previousRank: 14,
  movement: 'up',
  sales: 1980,
  description: 'A family moves into their dream home, only to discover its dark history.',
  spiceLevel: 1,
  darknessLevel: 5,
  formats: ['hardcover', 'ebook', 'audiobook']
}];
// Genre config with icons and colors
const genreConfig: Record<string, {
  icon: string;
  color: string;
}> = {
  Mystery: {
    icon: '🔍',
    color: 'text-indigo-500'
  },
  Thriller: {
    icon: '⚡',
    color: 'text-red-500'
  },
  Fantasy: {
    icon: '✨',
    color: 'text-violet-500'
  },
  Romance: {
    icon: '💕',
    color: 'text-pink-500'
  },
  Horror: {
    icon: '💀',
    color: 'text-gray-500'
  },
  'Science Fiction': {
    icon: '🚀',
    color: 'text-blue-500'
  },
  Adventure: {
    icon: '🗺️',
    color: 'text-orange-500'
  },
  'Historical Fiction': {
    icon: '📜',
    color: 'text-amber-600'
  },
  'Literary Fiction': {
    icon: '📖',
    color: 'text-emerald-500'
  },
  Crime: {
    icon: '🔎',
    color: 'text-slate-500'
  },
  Contemporary: {
    icon: '🌆',
    color: 'text-teal-500'
  },
  Supernatural: {
    icon: '👁️',
    color: 'text-purple-500'
  },
  Drama: {
    icon: '🎭',
    color: 'text-rose-500'
  }
};
// Get all unique genres from data
const getAllGenres = (books: typeof allBestsellers): string[] => {
  const genreSet = new Set<string>();
  books.forEach(book => book.genres.forEach(g => genreSet.add(g)));
  return Array.from(genreSet);
};
// Movement indicator
const MovementBadge = ({
  movement,
  previousRank
}: {
  movement: string;
  previousRank: number;
}) => {
  if (movement === 'new') {
    return <span className="text-[10px] font-semibold text-blue-400 bg-blue-500/20 px-1.5 py-0.5 rounded-full" data-id="element-6643" data-mp="2xs3xc49b4128">
        NEW
      </span>;
  }
  if (movement === 'up') {
    return <span className="flex items-center gap-0.5 text-[10px] font-semibold text-green-400" data-id="element-6644" data-mp="3qg9ksyrqw45r">
        <ArrowUpIcon className="h-2.5 w-2.5" data-id="element-6645" data-mp="3uhlfhiph7fkt" />
        {previousRank}
      </span>;
  }
  if (movement === 'down') {
    return <span className="flex items-center gap-0.5 text-[10px] font-semibold text-red-400" data-id="element-6646" data-mp="3ggr2emrhgqft">
        <ArrowDownIcon className="h-2.5 w-2.5" data-id="element-6647" data-mp="3lg3y7x0c17uj" />
        {previousRank}
      </span>;
  }
  return <span className="text-[10px] text-gray-400" data-id="element-6648" data-mp="252dskltkebis">—</span>;
};
// Ranked book carousel
function RankedCarousel({
  books,
  darkMode,
  startRank = 1
}: {
  books: typeof allBestsellers;
  darkMode: boolean;
  startRank?: number;
}) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(books.length > 4);
  const checkScroll = () => {
    if (carouselRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  const scroll = (dir: 'left' | 'right') => {
    if (carouselRef.current) {
      const amount = carouselRef.current.clientWidth * 0.8;
      carouselRef.current.scrollTo({
        left: carouselRef.current.scrollLeft + (dir === 'left' ? -amount : amount),
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 300);
    }
  };
  return <div className="relative" data-id="element-6649" data-mp="djr4cgoxmcrw">
      {canScrollLeft && <button onClick={() => scroll('left')} className={`absolute left-0 top-1/3 -translate-y-1/2 z-10 w-9 h-9 rounded-full shadow-lg flex items-center justify-center ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-800 hover:bg-gray-100'}`} data-id="element-6650" data-mp="iiuak4gnep1g">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-6651" data-mp="2cqofbah8qn1p" />
        </button>}

      <div ref={carouselRef} className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} onScroll={checkScroll} data-id="element-6652" data-mp="27zg366oj0nku">
        {books.map((book, idx) => {
        const displayRank = startRank + idx;
        return <div key={book.id} className="flex-shrink-0 w-[140px] sm:w-[160px]" data-id="element-6653" data-mp="2vnzbunbhn2dg" data-mpi={idx}>
              {/* Rank number above card */}
              <div className="flex items-center justify-between mb-1.5 px-0.5" data-id="element-6654" data-mp="2qqpbicmk96au">
                <span className={`text-2xl font-black leading-none ${displayRank === 1 ? 'text-amber-500' : displayRank === 2 ? darkMode ? 'text-gray-300' : 'text-gray-500' : displayRank === 3 ? 'text-amber-700' : darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-6655" data-mp="11bsoyr98ex5b">
                  #{displayRank}
                </span>
                <MovementBadge movement={book.movement} previousRank={book.previousRank} data-id="element-6656" data-mp="1v103c4hdknkj" />
              </div>
              <UnifiedBookCard book={book} data-id="element-6657" data-mp="2014oi7t8a6h3" />
              <div className={`mt-1 text-center text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-6658" data-mp="24zfwse3vk8x7">
                {book.weeksOnList} {book.weeksOnList === 1 ? 'wk' : 'wks'} on
                list
              </div>
            </div>;
      })}
      </div>

      {canScrollRight && <button onClick={() => scroll('right')} className={`absolute right-0 top-1/3 -translate-y-1/2 z-10 w-9 h-9 rounded-full shadow-lg flex items-center justify-center ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-800 hover:bg-gray-100'}`} data-id="element-6659" data-mp="2ipt54hkohpn2">
          <ChevronRightIcon className="h-4 w-4" data-id="element-6660" data-mp="h2ghr9olpdwe" />
        </button>}
    </div>;
}
export default function Bestsellers() {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  // Use the local bestsellers mock data which has all required fields
  // (reviewCount, sales, weeksOnList, movement, formats, etc.)
  const baseBooks = allBestsellers;
  const [timeframe, setTimeframe] = useState<'weekly' | 'monthly' | 'all-time'>('weekly');
  const [searchTerm, setSearchTerm] = useState('');
  // Sidebar filter state
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [selectedSpice, setSelectedSpice] = useState('');
  const allGenres = getAllGenres(baseBooks);
  const activeFilterCount = (timeframe !== 'weekly' ? 1 : 0) + selectedGenres.length + selectedFormats.length + (selectedSpice ? 1 : 0);
  const filterSections: FilterSection[] = [{
    id: 'timeframe',
    title: 'Timeframe',
    type: 'radio',
    options: [{
      value: 'weekly',
      label: 'This Week'
    }, {
      value: 'monthly',
      label: 'This Month'
    }, {
      value: 'all-time',
      label: 'All Time'
    }],
    value: timeframe,
    onChange: setTimeframe
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: allGenres.map(g => ({
      value: g,
      label: g
    })),
    value: selectedGenres,
    onChange: (val: string) => setSelectedGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'format',
    title: 'Format',
    type: 'checkbox',
    options: [{
      value: 'ebook',
      label: 'eBook'
    }, {
      value: 'paperback',
      label: 'Paperback'
    }, {
      value: 'hardcover',
      label: 'Hardcover'
    }, {
      value: 'audiobook',
      label: 'Audiobook'
    }],
    value: selectedFormats,
    onChange: (val: string) => setSelectedFormats(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'spice',
    title: 'Spice Level',
    type: 'radio',
    options: [{
      value: '',
      label: 'Any'
    }, {
      value: '1',
      label: '🌶 Mild (1)'
    }, {
      value: '2',
      label: '🌶🌶 Warm (2)'
    }, {
      value: '3',
      label: '🌶🌶🌶 Hot (3)'
    }, {
      value: '4',
      label: '🌶🌶🌶🌶 Steamy (4)'
    }, {
      value: '5',
      label: '🌶🌶🌶🌶🌶 Scorching (5)'
    }],
    value: selectedSpice,
    onChange: setSelectedSpice
  }];
  const resetFilters = () => {
    setTimeframe('weekly');
    setSelectedGenres([]);
    setSelectedFormats([]);
    setSelectedSpice('');
    setSearchTerm('');
  };
  // Apply filters to books
  const filteredBestsellers = baseBooks.filter(book => {
    if (selectedGenres.length > 0 && !book.genres.some(g => selectedGenres.includes(g))) return false;
    if (selectedFormats.length > 0 && !book.formats.some(f => selectedFormats.includes(f))) return false;
    if (selectedSpice && book.spiceLevel !== parseInt(selectedSpice)) return false;
    if (searchTerm && !book.title.toLowerCase().includes(searchTerm.toLowerCase()) && !book.author.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });
  const featuredBook = baseBooks[0];
  const filteredGenres = allGenres.filter(genre => filteredBestsellers.some(b => b.genres.includes(genre)));
  const getGenreBooks = (genre: string) => filteredBestsellers.filter(b => b.genres.includes(genre)).sort((a, b) => a.rank - b.rank);
  return <div className={`min-h-screen ${dk.page}`} data-id="element-6661" data-mp="20kmnze85rds8">
      {/* Page Header */}
      <div className={`py-6 px-4 ${dk.surface}`} data-id="element-6662" data-mp="1vn4n8b4ao4vb">
        <div className="max-w-7xl mx-auto" data-id="element-6663" data-mp="1vnw6qnkmac58">
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-6664" data-mp="1qvctjrpudj0y">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-6665" data-mp="1lwrqzln4an9c">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-6666" data-mp="1hwrdq4br8i3v" />
            <Link to="/browse" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-6667" data-mp="1cwmva8raddb0">
              Books
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-6668" data-mp="17zcxo5tqtvc3" />
            <span className={dk.textPrimary} data-id="element-6669" data-mp="2vurhubm2tp86">Bestsellers</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-6670" data-mp="30tb7alzvpj5a">
            <div className="flex items-center gap-3" data-id="element-6671" data-mp="2mtjom7yf3eky">
              <TrendingUpIcon className={`h-8 w-8 text-amber-500`} data-id="element-6672" data-mp="2mv4hzu8zwrh8" />
              <div data-id="element-6673" data-mp="2rqs769nh9kmq">
                <h1 className={`text-2xl md:text-3xl font-bold ${dk.textHeading}`} data-id="element-6674" data-mp="2rqa2qpqnw03q">
                  Bestsellers
                </h1>
                <p className={`mt-1 text-sm ${dk.textSecondary}`} data-id="element-6675" data-mp="2huywywx8e384">
                  The most-read books across every genre, ranked by readers.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-6676" data-mp="2855ldvx39o3v">
              <div className="relative flex-grow md:w-80" data-id="element-6677" data-mp="1u4mj75fb1c9m">
                <input type="text" placeholder="Search bestsellers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full px-5 py-2.5 pl-10 rounded-lg border focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-6678" data-mp="1u54jcd538amt" />
                <SearchIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${dk.textMuted}`} data-id="element-6679" data-mp="1pgv3jt7ja4fw" />
              </div>
              <Link to="/browse?sort=bestselling" className={`flex items-center px-4 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap ${dk.card} ${dk.textPrimary} border ${dk.border}`} data-id="element-6680" data-mp="1yg8gwwys3d5w">
                Browse All
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-6681" data-mp="2h6q5cpckvmlg">
        {/* Mobile Filter Button */}
        <div className="mb-6 lg:hidden" data-id="element-6682" data-mp="2d5o11u3t0kcm">
          <button onClick={() => setMobileFiltersOpen(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border shadow-sm ${dk.card} ${dk.textSecondary}`} data-id="element-6683" data-mp="2d6z760kq246w">
            <FilterIcon className="h-4 w-4" data-id="element-6684" data-mp="2kojiiiuxy7kq" />
            Filters
            {activeFilterCount > 0 && <span className={`ml-1 px-1.5 py-0.5 ${rt.badgeRadius} text-xs font-bold text-white bg-amber-600`} data-id="element-6685" data-mp="2yo17ay88lku3">
                {activeFilterCount}
              </span>}
          </button>
        </div>

        <div className="flex gap-8" data-id="element-6686" data-mp="21z3cjg0hlmwx">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-6687" data-mp="21wqm0egny489">
            <FilterSidebar sections={filterSections} onReset={resetFilters} data-id="element-6688" data-mp="1n8clwbamstve" />
          </FilterSidebarWrapper>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-12" data-id="element-6689" data-mp="1i8hrvyvk7o64">
            {/* #1 Bestseller Spotlight */}
            <section className={`rounded-xl overflow-hidden border ${dk.border} ${dk.card}`} data-id="element-6690" data-mp="3tvthyn1dkw1q">
              <div className="flex flex-col md:flex-row md:max-h-48" data-id="element-6691" data-mp="3kxhrcl0idy9a">
                <div className="w-full md:w-1/3 relative" data-id="element-6692" data-mp="3kv517kklkhl5">
                  <img src={featuredBook.coverImage} alt={featuredBook.title} className="w-full h-48 md:h-full object-cover" data-id="element-6693" data-mp="3pxduyj5annob" />
                  <span className="absolute top-3 left-3 text-white text-xs font-medium px-2 py-0.5 rounded-full bg-amber-600" data-id="element-6694" data-mp="lgt8la9x0c3c">
                    {featuredBook.genres[0]}
                  </span>
                  <span className="absolute top-3 right-3 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-6695" data-mp="cgd3xxhfqyww">
                    🏆 #1
                  </span>
                  <div className="absolute bottom-2 left-2 md:hidden flex items-center gap-1 bg-black/60 rounded-full px-2 py-0.5" data-id="element-6696" data-mp="2x8gf0mok22yq">
                    <StarIcon className="h-3 w-3 text-amber-400 fill-amber-400" data-id="element-6697" data-mp="2d7n52ubmc5zn" />
                    <span className="text-white text-xs font-semibold" data-id="element-6698" data-mp="294h1jufw4swv">
                      {featuredBook.rating}
                    </span>
                    <span className="text-white/70 text-xs" data-id="element-6699" data-mp="1zee0d6ym88ng">
                      ({featuredBook.reviewCount.toLocaleString()})
                    </span>
                  </div>
                </div>
                <div className={`w-full md:w-2/3 p-4 flex flex-col justify-between ${dk.card}`} data-id="element-6700" data-mp="1pg7z8rkmv6jn">
                  <div data-id="element-6701" data-mp="1lfeg0bi4yfx3">
                    <div className="flex items-center justify-between gap-2 mb-1" data-id="element-6702" data-mp="1lemr1jj1ngfs">
                      <div className="flex items-center gap-2" data-id="element-6703" data-mp="1ggl6sv9vtb2q">
                        <TrophyIcon className="h-4 w-4 text-amber-500" data-id="element-6704" data-mp="3fsxfvrzn2alk" />
                        <span className="text-xs font-semibold uppercase tracking-wide text-amber-500" data-id="element-6705" data-mp="3fq1gs0c21fvj">
                          #1 Bestseller
                        </span>
                      </div>
                      <span className={`text-xs ${dk.textMuted}`} data-id="element-6706" data-mp="3nbx0u22r0a71">
                        {featuredBook.weeksOnList} weeks on list ·{' '}
                        {featuredBook.sales.toLocaleString()} copies
                      </span>
                    </div>
                    <h2 className={`text-lg font-bold mb-0.5 ${dk.textHeading}`} data-id="element-6707" data-mp="vssagvdfe38">
                      {featuredBook.title}
                    </h2>
                    <p className={`text-xs mb-2 ${dk.textSecondary}`} data-id="element-6708" data-mp="34du8bdazrv4h">
                      by {featuredBook.author}
                    </p>
                    <p className={`text-xs mb-2 line-clamp-2 ${dk.textSecondary}`} data-id="element-6709" data-mp="3ddqyjjd1521n">
                      {featuredBook.description}
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-3" data-id="element-6710" data-mp="smytl0mtvhu3">
                    <div className="flex items-center gap-1" data-id="element-6711" data-mp="r6cqmlq1lfhj">
                      <StarIcon className="h-4 w-4 text-amber-400 fill-amber-400" data-id="element-6712" data-mp="r6ur5ujqmfv9" />
                      <span className={`text-sm font-semibold ${dk.textPrimary}`} data-id="element-6713" data-mp="m8jj9pd06ffo">
                        {featuredBook.rating}
                      </span>
                      <span className={`text-xs hidden md:inline ${dk.textMuted}`} data-id="element-6714" data-mp="w4mhik4qhtxc">
                        ({featuredBook.reviewCount.toLocaleString()} reviews)
                      </span>
                    </div>
                    <Link to={`/book/${featuredBook.id}`} className="flex items-center justify-center px-4 py-1.5 rounded-lg font-medium text-sm text-white transition-colors bg-amber-600 hover:bg-amber-500" data-id="element-6715" data-mp="1jvjmte20xszm">
                      View Book <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-6716" data-mp="1ou36t8x24upc" />
                    </Link>
                  </div>
                </div>
              </div>
            </section>

            {/* Overall Bestsellers Carousel */}
            <section data-id="element-6717" data-mp="2chqfe7glu5l6">
              <div className="flex items-center justify-between mb-4" data-id="element-6718" data-mp="2cg6to1c6j2c0">
                <div className="flex items-center gap-2" data-id="element-6719" data-mp="1yiib505l47a4">
                  <TrendingUpIcon className="h-6 w-6 text-amber-500" data-id="element-6720" data-mp="1yk1s6sz2emcy" />
                  <div data-id="element-6721" data-mp="23f8p9hxtwssw">
                    <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-6722" data-mp="23iwi0evg9b6k">
                      Overall Bestsellers
                    </h2>
                    <p className={`text-sm ${dk.textSecondary}`} data-id="element-6723" data-mp="1tivxiplugp0s">
                      Top books across all genres.
                    </p>
                  </div>
                </div>
                <Link to="/browse?sort=bestselling" className={`text-sm font-medium flex items-center text-amber-500 hover:text-amber-400`} data-id="element-6724" data-mp="1jsjdcyhxyxv6">
                  See all <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-6725" data-mp="1aso0mp8qmut1" />
                </Link>
              </div>
              <RankedCarousel books={filteredBestsellers} darkMode={dk.isDark} startRank={1} data-id="element-6726" data-mp="3chtxdk6tc4ve" />
            </section>

            {/* Genre-specific ranked carousels */}
            {filteredGenres.map(genre => {
            const genreBooks = getGenreBooks(genre);
            if (genreBooks.length === 0) return null;
            const config = genreConfig[genre] || {
              icon: '📚',
              color: 'text-indigo-500'
            };
            return <section key={genre} data-id="element-6727" data-mp="3rfckki3uea0q">
                  <div className="flex items-center justify-between mb-4" data-id="element-6728" data-mp="3mf82wor7798x">
                    <div className="flex items-center gap-2" data-id="element-6729" data-mp="3mhb0btuok4no">
                      <span className="text-xl" data-id="element-6730" data-mp="3hfvdpxcutq36">{config.icon}</span>
                      <div data-id="element-6731" data-mp="2b0ui5srqr9x4">
                        <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-6732" data-mp="2b14ahn09dt5z">
                          Top in {genre}
                        </h2>
                        <p className={`text-sm ${dk.textSecondary}`} data-id="element-6733" data-mp="1vxb3vxssekub">
                          {genreBooks.length} title
                          {genreBooks.length > 1 ? 's' : ''} ranked this period.
                        </p>
                      </div>
                    </div>
                    <Link to={`/browse?genre=${genre.toLowerCase().replace(/\s+/g, '-')}&sort=bestselling`} className={`text-sm font-medium flex items-center text-amber-500 hover:text-amber-400`} data-id="element-6734" data-mp="25tml9278i0vm">
                      See all <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-6735" data-mp="2jlawz4dkmnc2" />
                    </Link>
                  </div>
                  <RankedCarousel books={genreBooks} darkMode={dk.isDark} startRank={1} data-id="element-6736" data-mp="2ojvwf1l012zg" />
                </section>;
          })}

            {/* Bottom CTA */}
            <div className={`rounded-xl p-8 text-center border ${dk.border} ${dk.isDark ? 'bg-amber-900/20' : 'bg-amber-50'}`} data-id="element-6737" data-mp="z68zdh0gitoy">
              <BookOpenIcon className="h-12 w-12 mx-auto mb-4 text-amber-500" data-id="element-6738" data-mp="31280pmfrtrir" />
              <h2 className={`text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-6739" data-mp="2w57w7fykkwtr">
                Explore the Full Catalog
              </h2>
              <p className={`max-w-lg mx-auto mb-6 ${dk.textSecondary}`} data-id="element-6740" data-mp="35vjb1xjskqff">
                Browse thousands of books with advanced filters for genre,
                tropes, spice level, and more.
              </p>
              <Link to="/browse" className="inline-flex items-center px-6 py-3 rounded-lg font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5 bg-amber-600 hover:bg-amber-500" data-id="element-6741" data-mp="2d75apb58di0p">
                Browse All Books
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>;
}